// =============================================================================
// HUB OPÉRATION - Gestion des Commandes Matériel
// =============================================================================
// Affiche et gère les bons de commande depuis le devis signé
// =============================================================================

import { useState, useEffect, useCallback } from "react";
import {
  Package,
  Truck,
  CheckCircle2,
  Clock,
  AlertCircle,
  RefreshCw,
  Download,
  Plus,
  Edit,
  Trash2,
  ChevronDown,
  FileText,
  Calendar,
  Building2
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { formatDateFR } from "../../utils/dateFormat";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Couleurs par statut
const STATUT_COLORS = {
  a_commander: { bg: "bg-zinc-800/300/20", text: "text-gray-400", border: "border-gray-500/30" },
  commandee: { bg: "bg-blue-500/20", text: "text-blue-400", border: "border-blue-500/30" },
  en_preparation: { bg: "bg-yellow-500/20", text: "text-yellow-400", border: "border-yellow-500/30" },
  expediee: { bg: "bg-orange-500/20", text: "text-orange-400", border: "border-orange-500/30" },
  livree: { bg: "bg-green-500/20", text: "text-green-400", border: "border-green-500/30" },
  annulee: { bg: "bg-red-500/20", text: "text-red-400", border: "border-red-500/30" }
};

const STATUT_ICONS = {
  a_commander: Clock,
  commandee: FileText,
  en_preparation: Package,
  expediee: Truck,
  livree: CheckCircle2,
  annulee: AlertCircle
};

export default function CommandesManager({ dossierId, tenantId, devisSigne = false }) {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [commandes, setCommandes] = useState([]);
  const [statuts, setStatuts] = useState([]);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedCommande, setSelectedCommande] = useState(null);
  const [editForm, setEditForm] = useState({
    fournisseur: "",
    date_livraison_prevue: "",
    notes: "",
    statut: ""
  });

  // Charger les commandes
  const loadCommandes = useCallback(async () => {
    if (!dossierId) return;
    
    setLoading(true);
    try {
      const headers = { "X-Tenant-ID": tenantId || "climatpac" };
      
      // Charger statuts
      const statutsRes = await fetch(`${API}/api/operation/commandes/statuts`, { headers });
      if (statutsRes.ok) {
        setStatuts(await statutsRes.json());
      }
      
      // Charger commandes
      const res = await fetch(`${API}/api/operation/commandes/dossier/${dossierId}`, { headers });
      if (res.ok) {
        setCommandes(await res.json());
      }
    } catch (error) {
      console.error("Erreur chargement commandes:", error);
    } finally {
      setLoading(false);
    }
  }, [dossierId, tenantId]);

  useEffect(() => {
    loadCommandes();
  }, [loadCommandes]);

  // Générer les commandes depuis le devis
  const genererCommandes = async () => {
    if (!dossierId) return;
    
    setGenerating(true);
    try {
      const headers = { 
        "X-Tenant-ID": tenantId || "climatpac",
        "Content-Type": "application/json"
      };
      
      const res = await fetch(`${API}/api/operation/commandes/generer/${dossierId}`, {
        method: "POST",
        headers
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(data.message || "Commandes générées");
        loadCommandes();
      } else {
        toast.error(data.detail || "Erreur lors de la génération");
      }
    } catch (error) {
      console.error("Erreur génération:", error);
      toast.error("Erreur lors de la génération des commandes");
    } finally {
      setGenerating(false);
    }
  };

  // Mettre à jour le statut d'une commande
  const updateStatut = async (commandeId, nouveauStatut) => {
    try {
      const headers = { 
        "X-Tenant-ID": tenantId || "climatpac",
        "Content-Type": "application/json"
      };
      
      const res = await fetch(`${API}/api/operation/commandes/${commandeId}/statut`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ statut: nouveauStatut })
      });
      
      if (res.ok) {
        toast.success("Statut mis à jour");
        loadCommandes();
      } else {
        toast.error("Erreur lors de la mise à jour");
      }
    } catch (error) {
      console.error("Erreur update statut:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Sauvegarder les modifications
  const saveCommande = async () => {
    if (!selectedCommande) return;
    
    try {
      const headers = { 
        "X-Tenant-ID": tenantId || "climatpac",
        "Content-Type": "application/json"
      };
      
      const res = await fetch(`${API}/api/operation/commandes/${selectedCommande.id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(editForm)
      });
      
      if (res.ok) {
        toast.success("Commande mise à jour");
        setShowEditDialog(false);
        loadCommandes();
      } else {
        toast.error("Erreur lors de la sauvegarde");
      }
    } catch (error) {
      console.error("Erreur save:", error);
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  // Télécharger le PDF
  const downloadPdf = async (commandeId, numero) => {
    try {
      const headers = { "X-Tenant-ID": tenantId || "climatpac" };
      const res = await fetch(`${API}/api/operation/commandes/${commandeId}/pdf`, { headers });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `commande_${numero}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        toast.error("Erreur lors du téléchargement");
      }
    } catch (error) {
      console.error("Erreur download:", error);
      toast.error("Erreur lors du téléchargement");
    }
  };

  // Supprimer une commande
  const deleteCommande = async (commandeId) => {
    if (!confirm("Supprimer cette commande ?")) return;
    
    try {
      const headers = { "X-Tenant-ID": tenantId || "climatpac" };
      const res = await fetch(`${API}/api/operation/commandes/${commandeId}`, {
        method: "DELETE",
        headers
      });
      
      if (res.ok) {
        toast.success("Commande supprimée");
        loadCommandes();
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      console.error("Erreur delete:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  // Ouvrir le dialog d'édition
  const openEdit = (commande) => {
    setSelectedCommande(commande);
    setEditForm({
      fournisseur: commande.fournisseur || "",
      date_livraison_prevue: commande.date_livraison_prevue?.split("T")[0] || "",
      notes: commande.notes || "",
      statut: commande.statut || "a_commander"
    });
    setShowEditDialog(true);
  };

  // Obtenir l'icône du statut
  const getStatutIcon = (statut) => {
    const Icon = STATUT_ICONS[statut] || Clock;
    return Icon;
  };

  // Obtenir le label du statut
  const getStatutLabel = (statut) => {
    const s = statuts.find(st => st.code === statut);
    return s?.label || statut;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-white font-medium flex items-center gap-2">
          <Package className="w-5 h-5 text-orange-400" />
          Commandes Matériel
        </h4>
        <div className="flex gap-2">
          {commandes.length === 0 && (
            <Button
              size="sm"
              onClick={genererCommandes}
              disabled={generating || !devisSigne}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {generating ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Générer depuis devis
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={loadCommandes}
            disabled={loading}
            className="border-slate-600"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Message si pas de devis signé */}
      {!devisSigne && commandes.length === 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-sm">
          <p className="text-amber-400">
            Un devis signé est requis pour générer les bons de commande.
          </p>
        </div>
      )}

      {/* Liste des commandes */}
      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
        </div>
      ) : commandes.length === 0 ? (
        <div className="text-center py-8 bg-slate-700/30 rounded-lg">
          <Package className="w-12 h-12 mx-auto text-zinc-400 mb-3" />
          <p className="text-slate-400">Aucune commande</p>
          <p className="text-zinc-400 text-sm mt-1">
            {devisSigne 
              ? "Cliquez sur \"Générer depuis devis\" pour créer les bons de commande"
              : "Un devis signé est requis"
            }
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {commandes.map((cmd) => {
            const colors = STATUT_COLORS[cmd.statut] || STATUT_COLORS.a_commander;
            const StatusIcon = getStatutIcon(cmd.statut);
            
            return (
              <div 
                key={cmd.id}
                className={`bg-slate-700/30 rounded-lg p-4 border ${colors.border}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Numéro et statut */}
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-white font-mono font-medium">
                        {cmd.numero}
                      </span>
                      <Badge className={`${colors.bg} ${colors.text} border ${colors.border}`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {getStatutLabel(cmd.statut)}
                      </Badge>
                    </div>
                    
                    {/* Produit */}
                    <div className="mb-2">
                      <p className="text-white">{cmd.produit?.nom}</p>
                      {cmd.produit?.référence && (
                        <p className="text-slate-400 text-sm">Réf: {cmd.produit.référence}</p>
                      )}
                      {cmd.produit?.marque && (
                        <p className="text-slate-400 text-sm">Marque: {cmd.produit.marque}</p>
                      )}
                    </div>
                    
                    {/* Détails */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                      <div>
                        <span className="text-zinc-400">Quantité:</span>
                        <span className="text-white ml-2">{cmd.produit?.quantite || 1}</span>
                      </div>
                      <div>
                        <span className="text-zinc-400">Prix HT:</span>
                        <span className="text-white ml-2">
                          {cmd.produit?.prix_total_ht?.toLocaleString() || 0} €
                        </span>
                      </div>
                      <div>
                        <span className="text-zinc-400">Fournisseur:</span>
                        <span className="text-white ml-2">{cmd.fournisseur || "À définir"}</span>
                      </div>
                      {cmd.date_livraison_prevue && (
                        <div>
                          <span className="text-zinc-400">Livraison prévue:</span>
                          <span className="text-white ml-2">
                            {formatDateFR(cmd.date_livraison_prevue)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-1 ml-4">
                    {/* Changement rapide de statut */}
                    <Select
                      value={cmd.statut}
                      onValueChange={(value) => updateStatut(cmd.id, value)}
                    >
                      <SelectTrigger className="w-32 h-8 bg-slate-800 border-slate-600 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {statuts.map((s) => (
                          <SelectItem key={s.code} value={s.code}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => downloadPdf(cmd.id, cmd.numero)}
                      className="text-blue-400 hover:text-blue-300 h-8 w-8 p-0"
                      title="Télécharger PDF"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEdit(cmd)}
                      className="text-violet-400 hover:text-violet-300 h-8 w-8 p-0"
                      title="Modifier"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteCommande(cmd.id)}
                      className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Résumé */}
      {commandes.length > 0 && (
        <div className="flex items-center justify-between bg-slate-700/30 rounded-lg p-3 text-sm">
          <span className="text-slate-400">
            {commandes.length} commande(s) • 
            {commandes.filter(c => c.statut === "livree").length} livrée(s)
          </span>
          <span className="text-white font-medium">
            Total: {commandes.reduce((sum, c) => sum + (c.produit?.prix_total_ht || 0), 0).toLocaleString()} € HT
          </span>
        </div>
      )}

      {/* Dialog édition */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Modifier la commande
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedCommande && (
              <div className="bg-slate-800 rounded-lg p-3 mb-4">
                <p className="text-white font-medium">{selectedCommande.numero}</p>
                <p className="text-slate-400 text-sm">{selectedCommande.produit?.nom}</p>
              </div>
            )}
            
            <div>
              <Label className="text-slate-300">Fournisseur</Label>
              <Input
                value={editForm.fournisseur}
                onChange={(e) => setEditForm(prev => ({ ...prev, fournisseur: e.target.value }))}
                placeholder="Nom du fournisseur"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-slate-300">Date de livraison prévue</Label>
              <Input
                type="date"
                value={editForm.date_livraison_prevue}
                onChange={(e) => setEditForm(prev => ({ ...prev, date_livraison_prevue: e.target.value }))}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-slate-300">Statut</Label>
              <Select
                value={editForm.statut}
                onValueChange={(value) => setEditForm(prev => ({ ...prev, statut: value }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statuts.map((s) => (
                    <SelectItem key={s.code} value={s.code}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-slate-300">Notes</Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Notes sur la commande..."
                className="bg-slate-800 border-slate-700 text-white mt-1"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditDialog(false)}
              className="border-slate-600"
            >
              Annuler
            </Button>
            <Button
              onClick={saveCommande}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
